import './App.css'
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { setupIframeMessaging } from './lib/iframe-messaging';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { EstateProvider } from '@/lib/EstateContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import { useEffect } from 'react';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

setupIframeMessaging();

// Apply theme from localStorage on app initialization
const savedTheme = localStorage.getItem('theme') || 'light';
if (savedTheme === 'dark') {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, user, isAuthSettled } = useAuth();

  // Show loading spinner only on INITIAL auth check.
  // Once the user is authenticated, never blank the screen for background re-validations
  // (which would unmount in-progress forms like CreateEstate).
  if ((isLoadingPublicSettings || isLoadingAuth) && !isAuthenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Show login page instead of redirecting
      return (
        <Routes>
          <Route path="/Login" element={<Login />} />
          <Route path="/Signup" element={<Signup />} />
          <Route path="/ForgotPassword" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Public legal pages */}
          <Route path="/PrivacyPolicy" element={<Pages.PrivacyPolicy />} />
          <Route path="/TermsOfService" element={<Pages.TermsOfService />} />
          <Route path="/FAQ" element={<Pages.FAQ />} />
          <Route path="*" element={<Navigate to="/Login" replace />} />
        </Routes>
      );
    }
  }

  // Allow unauthenticated access to reset-password page
  if (!isAuthenticated && window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  // Check if user needs to complete role setup.
  // Guard with isAuthSettled so a DB timeout + empty cache doesn't redirect existing users.
  const needsRoleSetup = user && isAuthenticated && isAuthSettled && !user.user_role;

  // Allow access to RoleSetup and reset-password pages even if setup is needed
  // Use case-insensitive comparison to avoid redirect loops
  const currentPath = window.location.pathname.toLowerCase();
  const allowedPathsWithoutRole = ['/rolesetup', '/reset-password'];
  if (needsRoleSetup && !allowedPathsWithoutRole.includes(currentPath)) {
    return <Navigate to="/RoleSetup" replace />;
  }

  // Render the main app
  return (
    <Routes>
      {/* Public pages without layout/sidebar */}
      <Route path="/" element={<Pages.Home />} />
      <Route path="/PrivacyPolicy" element={<Pages.PrivacyPolicy />} />
      <Route path="/TermsOfService" element={<Pages.TermsOfService />} />
      <Route path="/FAQ" element={<Pages.FAQ />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* All other pages with layout/sidebar */}
      {Object.entries(Pages)
        .filter(([path]) => path !== 'Home' && path !== 'PrivacyPolicy' && path !== 'TermsOfService')
        .map(([path, Page]) => (
          <Route 
            key={path} 
            path={`/${path}`} 
            element={
              <LayoutWrapper currentPageName={path}>
                <Page />
              </LayoutWrapper>
            } 
          />
        ))}
      
      {/* 404 with layout */}
      <Route path="*" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <PageNotFound />
        </LayoutWrapper>
      } />
    </Routes>
  );
};


function App() {

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router 
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <NavigationTracker />
            <EstateProvider>
              <AuthenticatedApp />
            </EstateProvider>
          </Router>
          <Toaster />
          <VisualEditAgent />
        </QueryClientProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  )
}

// Fallback component for errors
const ErrorFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
    <div className="text-center p-8 max-w-md">
      <h1 className="text-2xl font-bold text-slate-900 mb-4">Something went wrong</h1>
      <p className="text-slate-600 mb-6">
        We've been notified and are working on a fix. Please try refreshing the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

export default App
